/**
 * /legal/dpa — Data Processing Agreement summary.
 *
 * Distils the controlling DPA template at:
 *   docs-vault/05-compliance/policies/contract-template--data-processing-agreement.md
 * into a reader-friendly summary. The countersigned, schedule-bound DPA is the
 * authoritative document for Practice and Enterprise tier customers; this page
 * is the public summary.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/legal/dpa')({
  component: LegalDpaPage,
});

const TOC = [
  { anchor: 'roles',          label: 'Roles of the parties' },
  { anchor: 'scope',          label: 'Scope and duration' },
  { anchor: 'subprocessors',  label: 'Sub-processors' },
  { anchor: 'transfers',      label: 'Cross-border transfers' },
  { anchor: 'security',       label: 'Technical and organisational measures' },
  { anchor: 'dsar',           label: 'Data subject requests' },
  { anchor: 'breach',         label: 'Breach notification' },
  { anchor: 'retention',      label: 'Retention and return' },
  { anchor: 'execute',        label: 'How to execute' },
];

function LegalDpaPage() {
  useEffect(() => {
    document.title = 'Data Processing Agreement · Elreign';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Elreign Data Processing Agreement summary: operator obligations under POPIA s24, sub-processor list, cross-border transfer safeguards, technical and organisational measures, and breach notification.',
    );
  }, []);

  return (
    <LegalLayout heading="Data Processing Agreement." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        This page summarises the Elreign Data Processing Agreement (DPA) for tenants evaluating
        the platform. The DPA forms Schedule C to the Master Services Agreement and is
        countersigned with every Practice and Enterprise tier customer at activation. The
        controlling document is the executed counterpart; this summary is offered for orientation.
      </p>

      <h2 id="roles">Roles of the parties</h2>
      <p>
        The tenant firm is the <strong>Responsible Party</strong> under POPIA section 1 and the
        Controller under the GDPR where the latter applies. Elreign is the <strong>Operator</strong>
        under POPIA and the Processor under the GDPR. We process personal information only on the
        tenant&apos;s documented instructions, except where law requires otherwise and lawful
        notification is given to the tenant beforehand.
      </p>

      <h2 id="scope">Scope and duration</h2>
      <p>
        Processing covers the administration of trusts on the platform: storage, transmission,
        retrieval, consultation, structuring, encryption, pseudonymisation, and erasure. The
        processing continues for the subscription term plus the post-termination export window
        and any retention period mandated by law (notably FICA s23, which requires beneficial
        ownership records to be retained for at least five years after the end of a business
        relationship).
      </p>

      <h2 id="subprocessors">Sub-processors</h2>
      <p>
        We use a small number of sub-processors, listed publicly at{' '}
        <Link to="/legal/subprocessors">/legal/subprocessors</Link>. The current list comprises
        Microsoft Azure (cloud compute and storage in South Africa North primary, South Africa
        West secondary), Supabase (authentication metadata only, in EU West), and Azure
        Communication Services (transactional email).
      </p>
      <p>
        We notify designated tenant contacts in writing at least 30 days before any addition to
        or change in the sub-processor list. The tenant may object; unresolved objections give
        the tenant a right to terminate under the Master Services Agreement.
      </p>

      <h2 id="transfers">Cross-border transfers</h2>
      <p>
        Substantive personal information (trust records, beneficial owner data, FICA documents,
        signing artefacts) is stored exclusively in South Africa. Authentication metadata is
        stored in Supabase EU West, which is permitted under POPIA s72 by reference to the
        European Union adequacy decision and is further protected by standard contractual
        clauses adopted under GDPR Article 46(2)(c).
      </p>

      <h2 id="security">Technical and organisational measures</h2>
      <p>
        Attachment 2 to the executed DPA describes the technical and organisational measures
        (TOMs) in detail. In summary:
      </p>
      <ul>
        <li>
          Data at rest encrypted with AES-256; personal information fields additionally encrypted
          client-side using Azure Cosmos DB Always Encrypted.
        </li>
        <li>Data in transit encrypted with TLS 1.2 minimum; HSTS enforced; TLS 1.0 / 1.1 disabled.</li>
        <li>Multi-factor authentication mandatory for all administrators; step-up enforced for signature events and bank-account changes.</li>
        <li>WORM-locked audit chain with hash-linked records, minimum five-year retention.</li>
        <li>Multi-tenant isolation enforced at storage layer (decision L13) and at managed-identity scope (decision L25).</li>
        <li>Independent penetration testing being procured as part of the pre-GA security programme; quarterly internal security review.</li>
      </ul>

      <h2 id="dsar">Data subject requests</h2>
      <p>
        Where a data subject contacts us directly with a POPIA request relating to a tenant
        end-client, we route the request to the tenant within 48 hours of receipt without
        responding to the data subject ourselves, in keeping with the operator boundary. Where
        the request relates to Elreign&apos;s own processing (for example, the data subject is
        an Elreign user), we fulfil it ourselves within the statutory 30-day window.
      </p>

      <h2 id="breach">Breach notification</h2>
      <p>
        On confirming a personal information breach involving tenant data, we notify the
        tenant&apos;s designated security contact within 72 hours, in the format and through the
        channels described in Attachment 3. The notification includes the nature of the breach,
        affected categories of personal information, likely consequences, and the measures taken
        or proposed.
      </p>

      <h2 id="retention">Retention and return</h2>
      <p>
        On termination of the subscription, the tenant has a 90-day export window during which
        all trust records may be exported in machine-readable form (JSON, PDF, signed
        attachments). At the end of the export window, and on the tenant&apos;s instruction,
        personal information is cryptographically erased under a four-of-four approval procedure
        (key-shredding scheme); the audit chain itself is retained for the FICA s23 minimum and is
        rendered unreadable as personal information through the same erasure.
      </p>

      <h2 id="execute">How to execute</h2>
      <p>
        Practice and Enterprise tier customers receive the executable DPA bundled with the Master
        Services Agreement at activation. To request the DPA in advance for legal review, write
        to <a href="mailto:legal@elreign.io">legal@elreign.io</a> with your firm&apos;s name and
        intended tier; we will send the current execution copy within one business day.
      </p>

      <p style={{ marginTop: 32 }}>
        See also: <Link to="/legal/subprocessors">Sub-processor list</Link>,{' '}
        <Link to="/legal/security">Security disclosure</Link>,{' '}
        <Link to="/compliance/popia">POPIA compliance posture</Link>.
      </p>
    </LegalLayout>
  );
}
